import { useState } from 'react';
import { ChevronDown, ChevronUp, SlidersHorizontal, RefreshCw, Info } from 'lucide-react';

const ETHNICITIES = [
  { value: '', label: 'Select ethnicity...' },
  { value: 'european', label: 'European / Non-Hispanic White' },
  { value: 'african', label: 'African / African American' },
  { value: 'east_asian', label: 'East Asian' },
  { value: 'south_asian', label: 'South Asian' },
  { value: 'hispanic', label: 'Hispanic / Latino' },
  { value: 'ashkenazi', label: 'Ashkenazi Jewish' },
  { value: 'middle_eastern', label: 'Middle Eastern' },
  { value: 'other', label: 'Other / Mixed' },
];

function ToggleGroup({ options, value, onChange }) {
  return (
    <div className="flex rounded-xl overflow-hidden border border-gray-200">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex-1 py-2 text-sm font-semibold transition-all ${
            value === opt.value
              ? 'text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
          style={value === opt.value ? { background: '#021f30' } : {}}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function UserInputPanel() {
  const [open, setOpen] = useState(false);
  const [zygosity, setZygosity] = useState('het');
  const [sex, setSex] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [saved, setSaved] = useState(false);

  const handleUpdate = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const hasInput = zygosity || sex || ethnicity;

  return (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden border border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(249,52,65,0.08)' }}>
            <SlidersHorizontal size={16} style={{ color: '#f93441' }} />
          </div>
          <div>
            <div className="font-semibold text-gray-900 text-sm">Clinical Context Enrichment</div>
            <div className="text-xs text-gray-500">Add patient data to improve classification accuracy</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasInput && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#f93441' }}>
              Active
            </span>
          )}
          {open ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="px-6 pb-6 flex flex-col gap-5 border-t border-gray-100 pt-5 animate-fade-in">
          {/* Info banner */}
          <div className="flex gap-2.5 p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-800 leading-relaxed">
            <Info size={14} className="text-blue-500 shrink-0 mt-0.5" />
            <p>
              Providing patient-specific information helps refine ACMG criteria, adjust population
              frequency thresholds, and compute sex-specific penetrance estimates. No data is stored or transmitted.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {/* Zygosity */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Zygosity</label>
              <ToggleGroup
                options={[
                  { value: 'het', label: 'Heterozygous' },
                  { value: 'hom', label: 'Homozygous' },
                ]}
                value={zygosity}
                onChange={setZygosity}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {zygosity === 'het'
                  ? 'Single copy — typical for AD conditions'
                  : 'Both copies affected — required for most AR conditions'}
              </p>
            </div>

            {/* Sex */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Biological Sex</label>
              <ToggleGroup
                options={[
                  { value: 'female', label: 'Female' },
                  { value: 'male', label: 'Male' },
                  { value: 'other', label: 'Other' },
                ]}
                value={sex}
                onChange={setSex}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Used for sex-specific penetrance & X-linked analysis
              </p>
            </div>
          </div>

          {/* Ethnicity */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Ethnicity / Ancestry</label>
            <select
              value={ethnicity}
              onChange={(e) => setEthnicity(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
            >
              {ETHNICITIES.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1.5">
              Adjusts allele frequency comparisons to population-matched gnomAD subsets
            </p>
          </div>

          {/* Update button */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleUpdate}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm transition-all hover:brightness-110 active:scale-95"
              style={{ background: '#f93441' }}
            >
              <RefreshCw size={14} />
              Update Analysis
            </button>
            {saved && (
              <span className="text-green-600 text-sm font-semibold animate-fade-in">
                Analysis updated!
              </span>
            )}
            <button
              onClick={() => { setZygosity('het'); setSex(''); setEthnicity(''); }}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
